import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { ThemeContext, UserContext } from '../Context';
import { formatReportNumber } from '../../utils/logClient';
import {
  AccessBanner,
  AuthorizedPeople,
  VisibilityChoice,
  getAccessPalette,
  getPrimaryButtonSx,
  getRestrictionMode,
  getSectionTitleSx,
  plural
} from '../tool/AccessRights';

function AccessRightsDialog({ open, onClose, reportData, onSaved }) {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isDark = theme === 'dark';
  const c = getAccessPalette(isDark);
  const me = typeof user === 'object' ? user?.matricule : user;

  const initialType = reportData?.access?.type === 'restricted' ? 'restricted' : 'public';
  const initialUsers = useMemo(
    () => (initialType === 'restricted' && Array.isArray(reportData?.access?.users) ? reportData.access.users : []),
    [initialType, reportData?.access?.users]
  );

  const [users, setUsers] = useState([]);
  const [restrictionMode, setRestrictionMode] = useState('none');
  const [loading, setLoading] = useState(open);
  const [loadError, setLoadError] = useState('');
  const [draftType, setDraftType] = useState(initialType);
  const [draftUsers, setDraftUsers] = useState(initialUsers);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Le brouillon repart de l'état enregistré à chaque ouverture
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraftType(initialType);
      setDraftUsers(initialUsers);
      setSaveError('');
      setLoading(true);
    }
  }

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const token = sessionStorage.getItem('authToken');
    setLoadError('');

    Promise.allSettled([
      axios.get('/api/user', { headers: { Authorization: `Bearer ${token}` } }),
      axios.get('/api/settings')
    ]).then(([usersResult, settingsResult]) => {
      if (cancelled) return;
      if (usersResult.status === 'fulfilled' && Array.isArray(usersResult.value.data)) {
        setUsers(usersResult.value.data);
      } else {
        setLoadError('Impossible de charger la liste des utilisateurs.');
      }
      // Sans paramètres lisibles, le serveur reste seul juge de l'interdiction
      setRestrictionMode(settingsResult.status === 'fulfilled' ? getRestrictionMode(settingsResult.value.data) : 'none');
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const meIsModerator = Boolean(users.find((person) => person.matricule === me)?.moderator);
  const publicForbidden = restrictionMode === 'always';
  const isRestrictedDraft = draftType === 'restricted';

  const draftSet = useMemo(() => new Set(draftUsers), [draftUsers]);
  const initialSet = useMemo(() => new Set(initialUsers), [initialUsers]);
  const added = initialType === 'restricted' ? draftUsers.filter((matricule) => !initialSet.has(matricule)) : [];
  const removed = initialType === 'restricted' && isRestrictedDraft
    ? initialUsers.filter((matricule) => !draftSet.has(matricule))
    : [];
  const typeChanged = draftType !== initialType;
  const hasChanges = typeChanged || (isRestrictedDraft && (added.length > 0 || removed.length > 0));
  const canSave = hasChanges &&
    !saving &&
    !loading &&
    !(isRestrictedDraft && draftUsers.length === 0) &&
    !(draftType === 'public' && publicForbidden);

  const selectType = (value) => {
    if (value === draftType) return;
    setDraftType(value);
    setSaveError('');
    if (value === 'restricted' && draftUsers.length === 0) {
      setDraftUsers(initialUsers.length > 0 ? initialUsers : (me ? [me] : []));
    }
  };

  const handleSave = async () => {
    if (!reportData?._id || !canSave) return;
    setSaving(true);
    setSaveError('');
    try {
      const token = sessionStorage.getItem('authToken');
      const { data } = await axios.put(
        `/api/report/${reportData._id}/access`,
        { type: draftType, users: isRestrictedDraft ? draftUsers : [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved?.(data.access);
      onClose();
    } catch (error) {
      setSaveError(error.response?.data?.message || 'Erreur lors de la mise à jour des droits d\'accès.');
    } finally {
      setSaving(false);
    }
  };

  const errorAlertSx = isDark ? {
    backgroundColor: c.restrictedSoft,
    color: c.restricted,
    '& .MuiAlert-icon': { color: c.restricted }
  } : {};

  const footerSummary = (() => {
    if (!hasChanges) return 'Aucune modification';
    if (typeChanged) {
      return draftType === 'public'
        ? 'Le document deviendra non restreint'
        : `Le document deviendra restreint · ${plural(draftUsers.length, 'personne autorisée', 'personnes autorisées')}`;
    }
    return [
      added.length > 0 && plural(added.length, 'ajout'),
      removed.length > 0 && plural(removed.length, 'retrait')
    ].filter(Boolean).join(' · ');
  })();

  const reportLabel = formatReportNumber(reportData?.number, reportData?.year);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          backgroundColor: c.dialogBg,
          backgroundImage: 'none',
          color: c.text,
          borderRadius: fullScreen ? 0 : '12px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          backgroundColor: c.surface,
          borderBottom: `1px solid ${c.border}`
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            backgroundColor: c.accentSoft,
            color: c.accent
          }}
        >
          <LockPersonIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: c.text, lineHeight: 1.3 }}>
            Droits d'accès
          </Typography>
          <Typography variant="body2" sx={{ color: c.textSecondary }}>
            {reportLabel ? `PV ${reportLabel}` : 'Document ouvert'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small" sx={{ color: c.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: '20px !important', pb: 2.5 }}>
        {initialType === 'restricted' ? (
          <AccessBanner c={c} tone="restricted" icon={LockIcon} title="Ce document est actuellement restreint">
            {initialUsers.length > 1
              ? `${initialUsers.length} personnes y ont accès, ainsi que les modérateurs.`
              : `${initialUsers.length} personne y a accès, ainsi que les modérateurs.`}
          </AccessBanner>
        ) : (
          <AccessBanner c={c} tone="open" icon={LockOpenIcon} title="Ce document est actuellement non restreint">
            Tous les utilisateurs peuvent le consulter et le modifier.
          </AccessBanner>
        )}

        <Typography variant="body2" sx={{ ...getSectionTitleSx(c, isDark), mt: 2.5, mb: 1 }}>
          Visibilité
        </Typography>
        <VisibilityChoice
          c={c}
          value={draftType}
          onChange={selectType}
          publicForbidden={publicForbidden}
          currentType={initialType}
          disabled={saving}
        />

        {publicForbidden && (
          <AccessBanner c={c} tone="warning" icon={BlockIcon} sx={{ mt: 1.25 }}>
            {initialType === 'public'
              ? 'L\'administrateur impose désormais que tous les documents soient restreints. Une fois restreint, ce document ne pourra plus redevenir non restreint.'
              : 'L\'administrateur impose que tous les documents soient restreints : ce document ne peut pas être rendu non restreint.'}
          </AccessBanner>
        )}

        {typeChanged && draftType === 'public' && (
          <AccessBanner c={c} tone="warning" icon={WarningAmberIcon} sx={{ mt: 1.25 }}>
            Après enregistrement, tous les utilisateurs pourront consulter et modifier ce document.
          </AccessBanner>
        )}

        {isRestrictedDraft && (
          <AuthorizedPeople
            c={c}
            isDark={isDark}
            users={users}
            loading={loading}
            value={draftUsers}
            onChange={setDraftUsers}
            me={me}
            lockSelf={!meIsModerator}
            creator={reportData?.createdBy}
            added={added}
            removed={removed}
            disabled={saving}
            sx={{ mt: 2.5 }}
          />
        )}

        {loadError && (
          <Alert severity="error" sx={{ mt: 2, ...errorAlertSx }}>
            {loadError}
          </Alert>
        )}
      </DialogContent>

      {saveError && (
        <Alert severity="error" onClose={() => setSaveError('')} sx={{ mx: 2.5, mb: 1, ...errorAlertSx }}>
          {saveError}
        </Alert>
      )}

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          backgroundColor: c.surface,
          borderTop: `1px solid ${c.border}`
        }}
      >
        <Typography variant="body2" sx={{ color: hasChanges ? c.text : c.textSecondary }}>
          {footerSummary}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button onClick={onClose} disabled={saving} sx={{ color: isDark ? c.cancelText : 'text.secondary' }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={getPrimaryButtonSx(c, isDark)}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AccessRightsDialog;
